import { Icon } from '@toggle/design-system';
import { formatPercentage } from '@toggle/helpers';
import { EventFeedEvent } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { getTierRelationText } from '~/hooks/use-insight-drawer/utils';
import { getCardDate } from '~/utils/card-utils/card-utils';
import { CardPill } from '~/views/stream/components/stream-card/components/card-pill/CardPill';
import { CardType } from '~/views/stream/components/stream-card/StreamCard';

import * as S from '../../OverviewEventFeed.styles';

export interface ScenarioRowProps {
  event: EventFeedEvent;
  isLast?: boolean;
}

export const ScenarioRow = ({ event, isLast }: ScenarioRowProps) => {
  const { t } = useTranslation(['whatsNew', 'insights']);
  const { openDrawer } = useInsightDrawer();

  const median = event.metadata.median_return ?? 0;
  const horizon = event.metadata.horizon;
  const stars = event.metadata.stars;
  const episodeCount = event.metadata.episode_count ?? 0;
  const isBullish = median >= 0;
  const eventDate = new Date(event.created_at);

  const relation = getTierRelationText(event.relevance.tier, t);

  const handleClick = () => {
    openDrawer({
      id: event.source_id,
      trackingProperties: { source: 'asset_overview' },
      relation,
    });
  };

  return (
    <S.FeedItemWrapper $isLast={isLast}>
      <S.FeedItem onClick={handleClick}>
        <S.FeedItemHeader>
          <CardPill type={CardType.Scenario} />
        </S.FeedItemHeader>
        <S.InsightTitle>{event.title}</S.InsightTitle>
        <S.StatsRow>
          <S.StatsContainer>
            {isBullish ? (
              <S.BullishValue>
                {formatPercentage(median, { suffix: '%', addPlus: true })}
              </S.BullishValue>
            ) : (
              <S.BearishValue>
                {formatPercentage(median, { suffix: '%', addPlus: true })}
              </S.BearishValue>
            )}
            {horizon && (
              <>
                <S.Dot />
                <S.StatValue>{horizon.toUpperCase()}</S.StatValue>
              </>
            )}
            {stars !== undefined && (
              <>
                <S.Dot />
                <S.StarsWrapper>
                  <Icon
                    iconName="Rating"
                    size={10}
                    fillColor="var(--icon-primary)"
                  />
                  <S.StarsLabel>{stars}</S.StarsLabel>
                </S.StarsWrapper>
              </>
            )}
            <S.VerticalDivider />
            <S.EpisodesLabel>
              {t('insights:insightCard.episode', { count: episodeCount })}
            </S.EpisodesLabel>
          </S.StatsContainer>
          <S.TimeAgo>{getCardDate(eventDate)}</S.TimeAgo>
        </S.StatsRow>
      </S.FeedItem>
    </S.FeedItemWrapper>
  );
};
